import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useBranding } from "@/contexts/BrandingContext";
import { nativeAuthRedirect } from "@/lib/native";

const ResetPasswordPage = () => {
  const [mode, setMode] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();
  const { fullLogo } = useBranding();
  const navigate = useNavigate();

  useEffect(() => {
    // Web: recovery token arrives in the URL hash. Native: the deep-link handler
    // sets the session then navigates here with ?recovery=1.
    const hash = window.location.hash;
    const recoveryFlag = new URLSearchParams(window.location.search).get("recovery") === "1";
    if (hash.includes("type=recovery") || recoveryFlag) {
      setMode("reset");
    }
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // On native, reopen the app via its custom scheme; on web, same-origin.
      redirectTo: nativeAuthRedirect("reset-callback") ?? `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setSent(true);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password updated", description: "You can now sign in with your new password." });
      navigate("/login");
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <img src={fullLogo} alt="VGV" className="h-44 mx-auto mb-8 object-contain" />
          <div className="w-16 h-16 rounded-full bg-brand-green-lt flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-brand-green" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h1>
          <p className="text-muted-foreground mb-6">
            If an account exists for {email}, you'll receive a password reset link.
          </p>
          <Link to="/login" className="text-sm text-brand-green-dk hover:text-brand-forest">
            ← Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/">
            <img src={fullLogo} alt="VGV" className="h-44 mx-auto mb-6 object-contain" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {mode === "request" ? "Forgot Password?" : "Set New Password"}
          </h1>
          <p className="text-muted-foreground text-sm">
            {mode === "request"
              ? "Enter your email and we'll send you a reset link."
              : "Enter your new password below."}
          </p>
        </div>

        <form onSubmit={mode === "request" ? handleRequestReset : handleResetPassword} className="space-y-5">
          {mode === "request" ? (
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  minLength={10}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 10 characters</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-forest hover:bg-brand-green-dk text-primary-foreground h-12"
          >
            {loading ? "Processing..." : mode === "request" ? "Send Reset Link" : "Update Password"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-muted-foreground hover:text-brand-forest transition-colors">
            ← Back to Sign In
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
